import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FlagIcon from "@mui/icons-material/Flag";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import SideBar from "../../Components/SideBar";
import { bonusApi } from "../../Components/bonusApi";
import { getProgramsList, getCoursesList } from "../marketing/marketingApi";
import { toast } from "react-toastify";
import styles from "../CreateEnquiryWizard.module.scss";

const METRICS = [
  { value: "conversion", label: "Conversions" },
  { value: "revenue", label: "Revenue" },
  { value: "conversion_rate", label: "Conversion Rate" },
];

const SCOPES = [
  { value: "overall", label: "Overall" },
  { value: "program", label: "Program" },
  { value: "course", label: "Course" },
];

const REWARD_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "gift", label: "Gift" },
  { value: "event", label: "Event" },
  { value: "custom", label: "Custom" },
];

const TOTAL_STEPS = 4;
const STEP_META = [
  { id: 1, short: "Basics", title: "Name & Scope", icon: <EmojiEventsIcon fontSize="small" /> },
  { id: 2, short: "Goal", title: "Goal & Dates", icon: <FlagIcon fontSize="small" /> },
  { id: 3, short: "Reward", title: "Reward", icon: <CardGiftcardIcon fontSize="small" /> },
  { id: 4, short: "Review", title: "Review & Submit", icon: <PreviewOutlinedIcon fontSize="small" /> },
];

const STEP_FIELDS = {
  1: ["name", "description", "metric", "scope_type", "scope_id"],
  2: ["goal", "start_date", "end_date"],
  3: ["reward_type", "reward_description"],
};

const getDefaultForm = () => ({
  name: "",
  description: "",
  metric: "conversion",
  scope_type: "overall",
  scope_id: "",
  goal: "",
  start_date: "",
  end_date: "",
  reward_type: "cash",
  reward_description: "",
});

export default function CreateBonusPage() {
  const navigate = useNavigate();
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState(getDefaultForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdName, setCreatedName] = useState("");

  const progressPercent = (currentStep / TOTAL_STEPS) * 100;

  useEffect(() => {
    let cancelled = false;
    getProgramsList()
      .then((data) => { if (!cancelled) setPrograms(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPrograms([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (formData.scope_type === "program" && formData.scope_id) {
      getCoursesList(Number(formData.scope_id))
        .then((data) => setCourses(Array.isArray(data) ? data : []))
        .catch(() => setCourses([]));
    } else if (formData.scope_type === "course") {
      getCoursesList(null)
        .then((data) => setCourses(Array.isArray(data) ? data : []))
        .catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [formData.scope_type, formData.scope_id]);

  useEffect(() => {
    setOptionsLoading(false);
  }, [programs]);

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "scope_type" ? { scope_id: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    setApiError("");
  };

  const validateStep1 = (data = formData) => {
    const nextErrors = {};
    if (!(data.name || "").trim()) nextErrors.name = "Bonus name is required.";
    if (data.scope_type === "program" && !data.scope_id) nextErrors.scope_id = "Please select a program.";
    if (data.scope_type === "course" && !data.scope_id) nextErrors.scope_id = "Please select a course.";
    return nextErrors;
  };

  const validateStep2 = (data = formData) => {
    const nextErrors = {};
    const goalNum = Number(data.goal);
    if (!data.goal || isNaN(goalNum) || goalNum <= 0) nextErrors.goal = "Goal must be a positive number.";
    if (!(data.start_date || "").trim()) nextErrors.start_date = "Start date is required.";
    if (!(data.end_date || "").trim()) nextErrors.end_date = "End date is required.";
    if (data.start_date && data.end_date && data.end_date < data.start_date) {
      nextErrors.end_date = "End date must be on or after start date.";
    }
    return nextErrors;
  };

  const applyStepErrors = (step, stepErrors) => {
    const stepFields = STEP_FIELDS[step] || [];
    setErrors((prev) => {
      const next = { ...prev };
      stepFields.forEach((f) => delete next[f]);
      return { ...next, ...stepErrors };
    });
  };

  const goToStep = (step) => setCurrentStep(step);

  const handleNext = () => {
    if (currentStep === 1) {
      const stepErrors = validateStep1();
      applyStepErrors(1, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep === 2) {
      const stepErrors = validateStep2();
      applyStepErrors(2, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const step1Errors = validateStep1();
    const step2Errors = validateStep2();
    applyStepErrors(1, step1Errors);
    applyStepErrors(2, step2Errors);
    if (Object.keys(step1Errors).length > 0) {
      setCurrentStep(1);
      return;
    }
    if (Object.keys(step2Errors).length > 0) {
      setCurrentStep(2);
      return;
    }
    setApiError("");
    setIsSubmitting(true);
    try {
      await bonusApi.create({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        metric: formData.metric,
        scope_type: formData.scope_type,
        scope_id: formData.scope_type === "overall" ? null : (formData.scope_id ? Number(formData.scope_id) : null),
        goal: Number(formData.goal),
        start_date: formData.start_date,
        end_date: formData.end_date,
        reward_type: formData.reward_type,
        reward_description: formData.reward_description.trim() || null,
      });
      setCreatedName(formData.name);
      setShowSuccessModal(true);
      toast.success("Bonus created. All employees have been notified.");
    } catch (err) {
      setApiError(err?.message || "Failed to create bonus.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(getDefaultForm());
    setErrors({});
    setCurrentStep(1);
    setShowSuccessModal(false);
    setCreatedName("");
  };

  const getScopeDisplay = () => {
    if (formData.scope_type === "overall") return "Overall";
    if (formData.scope_type === "program" && formData.scope_id) {
      const p = programs.find((x) => String(x.id) === String(formData.scope_id));
      return p ? p.name : `Program #${formData.scope_id}`;
    }
    if (formData.scope_type === "course" && formData.scope_id) {
      const c = courses.find((x) => String(x.id) === String(formData.scope_id));
      return c ? c.name : `Course #${formData.scope_id}`;
    }
    return "—";
  };

  const getMetricLabel = (value) => METRICS.find((m) => m.value === value)?.label ?? value;
  const getRewardLabel = (value) => REWARD_TYPES.find((r) => r.value === value)?.label ?? value;

  const renderFieldError = (field) =>
    errors[field] ? (
      <div className={styles.errorText} role="alert">
        {errors[field]}
      </div>
    ) : null;

  if (optionsLoading) {
    return (
      <Container fluid className={styles.page}>
        <Row className="g-0">
          <Col md={2} className="sidebar-column">
            <div className="sidebar-wrapper">
              <SideBar />
            </div>
          </Col>
          <Col md={10} className={styles.contentCol}>
            <div className={styles.wizardWrap}>
              <Card className={styles.wizardCard}>
                <Card.Body className={styles.cardBody}>
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" />
                    <span className="ms-2">Loading options...</span>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.page}>
      <Row className="g-0">
        <Col md={2} className="sidebar-column">
          <div className="sidebar-wrapper">
            <SideBar />
          </div>
        </Col>
        <Col md={10} className={styles.contentCol}>
          <div className={styles.wizardWrap}>
            <Card className={styles.wizardCard}>
              <Card.Body className={styles.cardBody}>
                <Button
                  type="button"
                  variant="link"
                  className={styles.gwBackBtn}
                  onClick={() => navigate("/bonuses")}
                >
                  ← Back to Bonuses
                </Button>
                <header className={styles.gwHeader}>
                  <div className={styles.gwTitleSection}>
                    <h2 className={styles.gwTitle}>Create Bonus</h2>
                    <p className={styles.gwSubtitle}>
                      Set up a new office bonus challenge. All employees will be notified by email.
                    </p>
                  </div>
                  <div className={styles.gwProgressIndicator}>
                    <div className={styles.gwProgressText}>Step {currentStep} of {TOTAL_STEPS}</div>
                    <ProgressBar now={progressPercent} className={styles.gwLinearProgress} aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`} />
                  </div>
                </header>

                <nav className={styles.gwNav} aria-label="Wizard steps">
                  <div className={styles.gwProgress}>
                    {STEP_META.map((step, idx) => {
                      const stepNumber = idx + 1;
                      const status = stepNumber < currentStep ? "done" : stepNumber === currentStep ? "current" : "locked";
                      const stepClass = status === "done" ? styles.gwStepDone : status === "current" ? styles.gwStepCurrent : styles.gwStepLocked;
                      const badgeClass = status === "done" ? styles.gwStepBadgeDone : status === "current" ? styles.gwStepBadgeCurrent : styles.gwStepBadgeLocked;
                      const shortClass = status === "done" ? styles.gwStepShortDone : status === "current" ? styles.gwStepShortCurrent : styles.gwStepShortLocked;
                      return (
                        <button
                          key={step.id}
                          type="button"
                          className={`${styles.gwStep} ${stepClass}`}
                          onClick={() => goToStep(stepNumber)}
                          disabled={stepNumber > currentStep}
                          aria-label={`Go to step ${stepNumber}: ${step.title}`}
                        >
                          <div className={`${styles.gwStepBadge} ${badgeClass}`}>
                            {status === "done" ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                          </div>
                          <div className={styles.gwStepLabel}>
                            <div className={`${styles.gwStepShort} ${shortClass}`}>{step.short}</div>
                            <div className={styles.gwStepTitle}>{step.title}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </nav>

                {apiError && <Alert variant="danger">{apiError}</Alert>}

                <Form onSubmit={currentStep === TOTAL_STEPS ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }} noValidate>
                  <section key={currentStep} className={styles.stepBody} aria-live="polite">
                    {currentStep === 1 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="name">
                            <Form.Label>Bonus Name *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFieldValue("name", e.target.value)}
                              isInvalid={Boolean(errors.name)}
                              placeholder="e.g. January Conversion Challenge"
                            />
                            {renderFieldError("name")}
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={formData.description}
                              onChange={(e) => setFieldValue("description", e.target.value)}
                              placeholder="Optional description for the team"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="metric">
                            <Form.Label>Metric *</Form.Label>
                            <Form.Select value={formData.metric} onChange={(e) => setFieldValue("metric", e.target.value)}>
                              {METRICS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="scope_type">
                            <Form.Label>Scope *</Form.Label>
                            <Form.Select value={formData.scope_type} onChange={(e) => setFieldValue("scope_type", e.target.value)}>
                              {SCOPES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        {formData.scope_type === "program" && (
                          <Col md={12}>
                            <Form.Group controlId="scope_id_program">
                              <Form.Label>Program *</Form.Label>
                              <Form.Select value={formData.scope_id} onChange={(e) => setFieldValue("scope_id", e.target.value)} isInvalid={Boolean(errors.scope_id)}>
                                <option value="">Select program...</option>
                                {programs.map((p) => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </Form.Select>
                              {renderFieldError("scope_id")}
                            </Form.Group>
                          </Col>
                        )}
                        {formData.scope_type === "course" && (
                          <Col md={12}>
                            <Form.Group controlId="scope_id_course">
                              <Form.Label>Course *</Form.Label>
                              <Form.Select value={formData.scope_id} onChange={(e) => setFieldValue("scope_id", e.target.value)} isInvalid={Boolean(errors.scope_id)}>
                                <option value="">Select course...</option>
                                {courses.map((c) => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </Form.Select>
                              {renderFieldError("scope_id")}
                            </Form.Group>
                          </Col>
                        )}
                      </Row>
                    )}

                    {currentStep === 2 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="goal">
                            <Form.Label>Goal Value *</Form.Label>
                            <Form.Control
                              type="number"
                              min={0.01}
                              step={formData.metric === "conversion_rate" ? 0.1 : 1}
                              value={formData.goal}
                              onChange={(e) => setFieldValue("goal", e.target.value)}
                              isInvalid={Boolean(errors.goal)}
                              placeholder={formData.metric === "conversion_rate" ? "Target % (e.g. 25)" : formData.metric === "revenue" ? "Target amount" : "Target count"}
                            />
                            <Form.Text className="text-muted">
                              {formData.metric === "conversion_rate" ? "Target conversion rate %." : formData.metric === "revenue" ? "Total revenue to reach." : "Number of conversions to reach."}
                            </Form.Text>
                            {renderFieldError("goal")}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="start_date">
                            <Form.Label>Start Date *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.start_date}
                              onChange={(e) => setFieldValue("start_date", e.target.value)}
                              isInvalid={Boolean(errors.start_date)}
                            />
                            {renderFieldError("start_date")}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="end_date">
                            <Form.Label>End Date *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.end_date}
                              min={formData.start_date || undefined}
                              onChange={(e) => setFieldValue("end_date", e.target.value)}
                              isInvalid={Boolean(errors.end_date)}
                            />
                            {renderFieldError("end_date")}
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 3 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="reward_type">
                            <Form.Label>Reward Type *</Form.Label>
                            <Form.Select value={formData.reward_type} onChange={(e) => setFieldValue("reward_type", e.target.value)}>
                              {REWARD_TYPES.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group controlId="reward_description">
                            <Form.Label>Reward Description</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.reward_description}
                              onChange={(e) => setFieldValue("reward_description", e.target.value)}
                              placeholder="e.g. Team lunch, $500 bonus"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 4 && (
                      <div className={styles.reviewWrap}>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Basics</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(1)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Name</span>
                                <span className={styles.reviewValue}>{formData.name || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Description</span>
                                <span className={styles.reviewValue}>{formData.description || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Metric</span>
                                <span className={styles.reviewValue}>{getMetricLabel(formData.metric)}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Scope</span>
                                <span className={styles.reviewValue}>{getScopeDisplay()}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Goal & Dates</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(2)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Goal</span>
                                <span className={styles.reviewValue}>{formData.goal || "—"}{formData.metric === "conversion_rate" ? "%" : ""}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Start Date</span>
                                <span className={styles.reviewValue}>{formData.start_date || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>End Date</span>
                                <span className={styles.reviewValue}>{formData.end_date || "—"}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Reward</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(3)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Type</span>
                                <span className={styles.reviewValue}>{getRewardLabel(formData.reward_type)}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Description</span>
                                <span className={styles.reviewValue}>{formData.reward_description || "—"}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </section>

                  <div className={styles.actions}>
                    <Button type="button" variant="outline-secondary" onClick={currentStep === 1 ? () => navigate("/bonuses") : handleBack} disabled={isSubmitting}>
                      {currentStep === 1 ? "Cancel" : "Back"}
                    </Button>
                    {currentStep < TOTAL_STEPS ? (
                      <Button type="button" variant="primary" onClick={handleNext} disabled={isSubmitting}>
                        Next
                      </Button>
                    ) : (
                      <Button variant="primary" type="submit" disabled={isSubmitting} className={styles.submitBtn}>
                        {isSubmitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className={styles.loadingText}>Creating...</span>
                          </>
                        ) : (
                          "Create Bonus"
                        )}
                      </Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      <Modal show={showSuccessModal} onHide={resetForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bonus created</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.successModalBody}>
          <div className={styles.successIcon} aria-hidden="true" />
          <p className={styles.successText}>Bonus created successfully</p>
          <p className={styles.successId}>
            Name: <strong>{createdName}</strong>
          </p>
          <p className="text-muted small mb-0">All employees have been notified by email.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => navigate("/bonuses")}>
            Back to Bonuses
          </Button>
          <Button variant="primary" onClick={resetForm}>
            Create another
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
